/*
* 通过测试发现以下问题，
* 1.导航栏的key只能使用英文，无法使用中文， 如：
*   自动生效配置，即直接在页面的definePage中设置 ‘%key%’ ： test: '测试标题'
*   不会自动生效，但可以通过uni.setNavigationBarTitle代码设置 ： '测试': '测试标题'
* 2. 使用方式：
*   方法一：通过 useI18n 系统方式，
*     import { useI18n } from 'vue-i18n';
*     const { t } = useI18n();
*     这个方法可以在.vue中使用，在script和template中直接使用t('key')
*
*   方法二：项目封装的$t, 这个方式只能在.vue的template中使用 $t('key')
*   这个方法是对方法一的封装，可直接使用，不用在页面中引入
*
*   方法三：项目封装的t方法
*   import { t } from '@/locale';
*   这个方法可以在.vue/ts/js文件中所有场景使用
*
*   同时这几个都可以传入参数：
*   1. 'weight': '{heavy}公斤'
*         使用 t('weight', {heavy: 100}) = '100公斤' 传对象，三种方法都支持
*   2. 'detail': '{0}cm, {1}KG'
*         使用 t('detail', [50，10]) = '50cm 10KG' 转数组，三种方法都支持
*   3. 'introduction': 'I am {name},height:{detail.height},weight:{detail.weight}'
*         使用 t('introduction', { name: '张三', detail: { height: 178, weight: '75kg' }}) = I am 张三,height:178,weight:75kg
*         传入多层对象，方法一，方法二会报错无法识别detail，方法三可正常使用
*
* */
import { createI18n } from 'vue-i18n';
import { LOCALE_KEY } from '@/enums/cacheEnum';
import { Storage } from '@/utils/storage';

import enUS from './en-US';

import zhCN from './zh-CN';

export enum LocaleEnum {
  ZH_CN = 'zh-Hans',
  EN_US = 'en',
}

const messages = {
  'en': enUS,
  'zh-Hans': zhCN, // key 不能乱写，查看uniapp官网
};

const i18n = createI18n({
  locale: uni.getLocale(), // 获取已设置的语言，fallback 语言需要再 manifest.config.ts 中设置
  messages,
  allowComposition: true,
  globalInjection: true, // 允许在非组件中使用i18n.global
});

/**
 * 可以拿到原始的语言模板，非 vue 文件使用这个方法，
 * @param { string } key 多语言的key，eg: "app.name"
 * @returns {string} 返回原始的多语言模板，eg: "{heavy}KG"
 */
export function getTemplateByKey(key: string) {
  if (!key) {
    console.error(`[i18n] Function getTemplateByKey(), key param is required`);
    return '';
  }
  const locale = uni.getLocale();

  const message = messages[locale]; // 拿到某个多语言的所有模板（是一个对象)
  if (Object.keys(message).includes(key)) {
    return message[key];
  }

  try {
    const keyList = key.split('.');
    return keyList.reduce((pre, cur) => {
      return pre[cur];
    }, message);
  }
  catch (error) {
    console.error(`[i18n] Function getTemplateByKey(), key param ${key} is not existed.`);
    return '';
  }
}

/**
 * formatI18n('我是{name},身高{detail.height},体重{detail.weight}',{name:'张三',detail:{height:178,weight:'75kg'}})
 * 暂不支持数组
 * @param template 多语言模板字符串，eg: `我是{name}`
 * @param {object | undefined} data 需要传递的数据对象，里面的key与多语言字符串对应，eg: `{name:'菲鸽'}`
 * @returns
 */
function formatI18n(template: string, data?: any) {
  return template.replace(/\{([^}]+)\}/g, (match, key: string) => {
    // console.log( match, key) // => { detail.height }  detail.height
    const arr = key.trim().split('.');
    let result = data;
    while (arr.length) {
      const first = arr.shift();
      result = result[first];
    }
    return result;
  });
}

/**
 * t('introduction',{name:'张三',detail:{height:178,weight:'75kg'}})
 * => formatI18n('我是{name},身高{detail.height},体重{detail.weight}',{name:'张三',detail:{height:178,weight:'75kg'}})
 * 没有key的，可以不传 data；暂不支持数组
 * @param template 多语言模板字符串，eg: `我是{name}`
 * @param {object | undefined} data 需要传递的数据对象，里面的key与多语言字符串对应，eg: `{name:'菲鸽'}`
 * @returns
 */
export function t(key, data?) {
  return formatI18n(getTemplateByKey(key), data);
}

export function useLanguage() {
  const currentLanguage = ref(i18n.global.locale);

  const setLanguage = (lang: LocaleEnum) => {
    console.log('setLanguage', lang);
    currentLanguage.value = lang;
    i18n.global.locale = lang;
    uni.setLocale(lang);
    Storage.set(LOCALE_KEY, lang);
  };

  return {
    currentLanguage,
    setLanguage,
  };
}

/* 非vue文件，使用这个方法进行调用 */
export function globalT(key: string) {
  return i18n.global.t(key);
}

export default i18n;
